<template>
  <div class="marketing-page event-manager">
    <MarketingToolHeader
      eyebrow="Event Marketing"
      title="Events"
      description="Plane Events für den Slider, steuere Anmeldungen und behalte Teilnehmer sowie Sichtbarkeit im Blick."
      icon="mdi-party-popper">
      <template v-slot:actions>
        <v-btn icon class="icon-action" :loading="loading" aria-label="Events aktualisieren" title="Events aktualisieren" @click="loadData"
          ><v-icon>mdi-refresh</v-icon></v-btn
        >
        <v-btn color="#009640" depressed class="primary-action" @click="createNewParty"><v-icon left>mdi-plus</v-icon>Neues Event</v-btn>
      </template>
    </MarketingToolHeader>

    <section class="metric-grid" aria-label="Event Übersicht">
      <v-card dark flat class="metric-card"
        ><span class="metric-icon icon-green"><v-icon>mdi-calendar-star</v-icon></span>
        <div>
          <span>Bevorstehend</span><strong>{{ loading ? "–" : upcomingParties.length }}</strong
          ><small>nächste Events</small>
        </div></v-card
      >
      <v-card dark flat class="metric-card"
        ><span class="metric-icon"><v-icon>mdi-calendar-multiple</v-icon></span>
        <div>
          <span>Gesamt</span><strong>{{ loading ? "–" : parties.length }}</strong
          ><small>alle Events</small>
        </div></v-card
      >
      <v-card dark flat class="metric-card"
        ><span class="metric-icon icon-blue"><v-icon>mdi-account-multiple-outline</v-icon></span>
        <div>
          <span>Anmeldungen</span><strong>{{ loading ? "–" : totalSignups }}</strong
          ><small>über alle Events</small>
        </div></v-card
      >
      <v-card dark flat class="metric-card"
        ><span class="metric-icon icon-amber"><v-icon>mdi-ticket-confirmation-outline</v-icon></span>
        <div>
          <span>Mit Anmeldung</span><strong>{{ loading ? "–" : signupEventCount }}</strong
          ><small>Registrierung aktiv</small>
        </div></v-card
      >
    </section>

    <v-card dark flat class="content-surface">
      <div class="surface-header">
        <div>
          <div class="surface-eyebrow">Eventkalender</div>
          <h2>Bevorstehende Events</h2>
          <p>Die nächsten Termine inklusive Ort und Anmeldestatus.</p>
        </div>
        <span class="count-pill">{{ upcomingParties.length }} geplant</span>
      </div>

      <div v-if="upcomingParties.length" class="event-grid">
        <v-card
          v-for="item in upcomingParties"
          :key="item._id"
          dark
          flat
          class="event-card"
          role="button"
          tabindex="0"
          @click="editParty(item)"
          @keydown.enter="editParty(item)">
          <div class="event-media">
            <v-img
              v-if="item.image"
              :src="'/img/' + item.image"
              height="210"
              gradient="to top, rgba(16,16,17,.92), rgba(16,16,17,.05) 72%"
              class="event-image">
              <h3>{{ item.title }}</h3>
            </v-img>
            <div v-else class="image-placeholder">
              <v-icon>mdi-image-off-outline</v-icon>
              <h3>{{ item.title }}</h3>
            </div>
            <span class="status-pill is-upcoming"><v-icon small>mdi-calendar-clock</v-icon>Geplant</span>
          </div>
          <div class="event-content">
            <div class="event-facts">
              <span><v-icon small>mdi-calendar-outline</v-icon>{{ $moment(item.eventDate).format("DD.MM.YYYY · HH:mm") }}</span>
              <span><v-icon small>mdi-map-marker-outline</v-icon>{{ item.location || "Ort noch offen" }}</span>
            </div>
            <div class="card-footer">
              <span v-if="item.allowSignup" class="signup-pill"
                ><v-icon small>mdi-account-check-outline</v-icon>{{ item.signups ? item.signups.length : 0
                }}<template v-if="item.maxSignups > 0"> / {{ item.maxSignups }}</template> Anmeldungen</span
              >
              <span v-else class="muted-pill"><v-icon small>mdi-account-off-outline</v-icon>Ohne Anmeldung</span>
              <v-icon small>mdi-arrow-right</v-icon>
            </div>
          </div>
        </v-card>
      </div>

      <div v-else-if="!loading" class="surface-empty">
        <span><v-icon>mdi-calendar-plus-outline</v-icon></span>
        <h3>Keine Events geplant</h3>
        <p>Erstelle den nächsten Termin für den Slider.</p>
        <v-btn outlined color="#58d388" class="secondary-action" @click="createNewParty"><v-icon left>mdi-plus</v-icon>Event erstellen</v-btn>
      </div>
    </v-card>

    <v-card v-if="pastParties.length" dark flat class="content-surface archive-surface">
      <div class="surface-header">
        <div>
          <div class="surface-eyebrow">Historie</div>
          <h2>Vergangene Events</h2>
          <p>Frühere Termine und ihre Anmeldungen bleiben zugänglich.</p>
        </div>
        <span class="count-pill">{{ pastParties.length }} vergangen</span>
      </div>
      <div class="archive-grid">
        <v-card
          v-for="item in pastParties"
          :key="item._id"
          dark
          flat
          class="archive-card"
          role="button"
          tabindex="0"
          @click="editParty(item)"
          @keydown.enter="editParty(item)">
          <v-img v-if="item.image" :src="'/img/' + item.image" width="112" height="88" class="archive-image" />
          <div v-else class="archive-placeholder"><v-icon>mdi-image-off-outline</v-icon></div>
          <div>
            <span><v-icon small>mdi-calendar-check-outline</v-icon>{{ $moment(item.eventDate).format("DD.MM.YYYY") }}</span>
            <h3>{{ item.title }}</h3>
          </div>
          <v-icon small>mdi-arrow-right</v-icon>
        </v-card>
      </div>
    </v-card>

    <v-dialog v-model="showEditDialog" fullscreen scrollable transition="dialog-bottom-transition">
      <v-card dark class="event-editor">
        <header class="event-editor-header">
          <span class="editor-mark"><v-icon>mdi-party-popper</v-icon></span>
          <div class="editor-title">
            <div class="surface-eyebrow">Event Editor</div>
            <h1>{{ party._id ? "Event bearbeiten" : "Neues Event erstellen" }}</h1>
            <p>Inhalt, Anmeldung und Sichtbarkeit konfigurieren.</p>
          </div>
          <div class="editor-header-actions">
            <v-btn text class="cancel-action" @click="showEditDialog = false">Abbrechen</v-btn>
            <v-btn color="#009640" depressed class="primary-action" :loading="saving" @click="saveParty"
              ><v-icon left>mdi-content-save-outline</v-icon>Speichern</v-btn
            >
            <v-btn icon class="icon-action" aria-label="Dialog schliessen" @click="showEditDialog = false"><v-icon>mdi-close</v-icon></v-btn>
          </div>
        </header>

        <v-card-text class="event-editor-body">
          <div class="editor-layout">
            <div class="editor-column media-column">
              <section class="editor-section">
                <div class="section-heading">
                  <span class="section-icon"><v-icon>mdi-image-outline</v-icon></span>
                  <div>
                    <div class="section-eyebrow">Medien</div>
                    <h2>Event-Bilder</h2>
                    <p>Slider und Hochformat-Overlay separat pflegen.</p>
                  </div>
                </div>
                <div class="media-upload-list">
                  <div class="cropper-panel">
                    <div class="cropper-heading"><strong>Slider</strong><span>Querformat · 3:1</span></div>
                    <slim-cropper :options="slimOptionsLandscape" class="event-cropper">
                      <img v-if="party.image" :src="'/img/' + party.image" alt="Event Slider Bild" />
                      <input type="file" accept="image/*" />
                    </slim-cropper>
                  </div>
                  <div class="cropper-panel portrait-panel">
                    <div class="cropper-heading"><strong>Overlay</strong><span>Hochformat · 9:16</span></div>
                    <slim-cropper :options="slimOptionsPortrait" class="event-cropper portrait-cropper">
                      <img v-if="party.imagePortrait" :src="'/img/' + party.imagePortrait" alt="Event Overlay Bild" />
                      <input type="file" accept="image/*" />
                    </slim-cropper>
                  </div>
                </div>
              </section>

              <section v-if="party.allowSignup && party._id" class="editor-section">
                <div class="section-heading section-heading-actions">
                  <span class="section-icon"><v-icon>mdi-account-multiple-outline</v-icon></span>
                  <div>
                    <div class="section-eyebrow">Anmeldungen</div>
                    <h2>Teilnehmerliste</h2>
                    <p>{{ party.signups ? party.signups.length : 0 }} registrierte Personen</p>
                  </div>
                  <v-btn icon class="icon-action" :loading="loading" aria-label="Teilnehmer aktualisieren" @click="loadData"
                    ><v-icon>mdi-refresh</v-icon></v-btn
                  >
                </div>
                <v-data-table
                  :headers="signupHeaders"
                  :items="party.signups"
                  :items-per-page="5"
                  dark
                  class="participant-table"
                  no-data-text="Noch keine Anmeldungen vorhanden.">
                  <template v-slot:item.email="{ item }"
                    ><span class="participant-email">{{ item.email }}</span></template
                  >
                </v-data-table>
              </section>
            </div>

            <div class="editor-column main-column">
              <section class="editor-section">
                <div class="section-heading">
                  <span class="section-icon"><v-icon>mdi-text-box-outline</v-icon></span>
                  <div>
                    <div class="section-eyebrow">Grunddaten</div>
                    <h2>Event-Informationen</h2>
                    <p>Titel, Datum, Ort und Beschreibung.</p>
                  </div>
                </div>
                <v-text-field v-model="party.title" label="Event Titel" outlined dark class="modern-field" hide-details="auto" />
                <v-row dense>
                  <v-col cols="12" md="6"
                    ><v-text-field
                      v-model="formattedEventDate"
                      type="datetime-local"
                      label="Event Datum & Zeit"
                      prepend-inner-icon="mdi-calendar-clock"
                      outlined
                      dark
                      class="modern-field"
                      hide-details="auto"
                  /></v-col>
                  <v-col cols="12" md="6"
                    ><v-text-field
                      v-model="party.location"
                      label="Ort / Studio"
                      prepend-inner-icon="mdi-map-marker-outline"
                      outlined
                      dark
                      class="modern-field"
                      hide-details="auto"
                  /></v-col>
                </v-row>
                <marketing-wysiwyg v-model="party.description" :editor-toolbar="customToolbar" />
              </section>

              <section class="editor-section">
                <div class="section-heading">
                  <span class="section-icon"><v-icon>mdi-account-check-outline</v-icon></span>
                  <div>
                    <div class="section-eyebrow">Registrierung</div>
                    <h2>Anmeldung</h2>
                    <p>Zielgruppe und Teilnehmerlimit festlegen.</p>
                  </div>
                </div>
                <div class="option-grid">
                  <div class="option-card">
                    <span>Zielgruppe</span
                    ><v-switch v-model="party.employeeOnly" color="#009640" inset label="Nur für Mitarbeiter" hide-details class="modern-switch" />
                  </div>
                  <div class="option-card">
                    <span>Anmeldung</span
                    ><v-switch v-model="party.allowSignup" color="#009640" inset label="Anmeldung erlauben" hide-details class="modern-switch" />
                  </div>
                  <div v-if="party.allowSignup" class="option-card">
                    <span>Kapazität</span
                    ><v-text-field
                      v-model.number="party.maxSignups"
                      type="number"
                      min="0"
                      label="Max. Teilnehmer (0 = offen)"
                      outlined
                      dense
                      dark
                      class="modern-field capacity-field"
                      hide-details="auto" />
                  </div>
                </div>
              </section>

              <section class="editor-section">
                <div class="section-heading">
                  <span class="section-icon"><v-icon>mdi-eye-outline</v-icon></span>
                  <div>
                    <div class="section-eyebrow">Ausspielung</div>
                    <h2>Sichtbarkeit</h2>
                    <p>Zeitraum und Zielstudios des Slider-Eintrags.</p>
                  </div>
                </div>
                <v-row dense>
                  <v-col cols="12" sm="6">
                    <v-menu v-model="menuShow" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                      <template v-slot:activator="{ on, attrs }"
                        ><v-text-field
                          v-model="party.showAfter"
                          label="Anzeigen ab"
                          prepend-inner-icon="mdi-calendar-start"
                          readonly
                          outlined
                          dark
                          class="modern-field"
                          hide-details="auto"
                          v-bind="attrs"
                          v-on="on"
                      /></template>
                      <v-date-picker v-model="party.showAfter" color="#009640" dark no-title scrollable locale="de" @input="menuShow = false" />
                    </v-menu>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-menu v-model="menuHide" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                      <template v-slot:activator="{ on, attrs }"
                        ><v-text-field
                          v-model="party.hideAfter"
                          label="Ausblenden ab"
                          prepend-inner-icon="mdi-calendar-end"
                          readonly
                          outlined
                          dark
                          class="modern-field"
                          hide-details="auto"
                          v-bind="attrs"
                          v-on="on"
                      /></template>
                      <v-date-picker v-model="party.hideAfter" color="#009640" dark no-title scrollable locale="de" @input="menuHide = false" />
                    </v-menu>
                  </v-col>
                </v-row>
                <v-select
                  v-model="party.studios"
                  :items="studios"
                  item-text="name"
                  item-value="_id"
                  label="Sichtbar in Studios"
                  multiple
                  chips
                  outlined
                  dark
                  class="modern-field"
                  hide-details="auto" />
              </section>
            </div>
          </div>
        </v-card-text>

        <footer class="mobile-save-bar">
          <v-btn text class="cancel-action" @click="showEditDialog = false">Abbrechen</v-btn
          ><v-btn color="#009640" depressed class="primary-action" :loading="saving" @click="saveParty">Speichern</v-btn>
        </footer>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "@/api"
import Slim from "../../../plugins/slimImage/slim/slim.vue"
import moment from "moment"
import MarketingToolHeader from "./MarketingToolHeader.vue"
import MarketingWysiwyg from "./MarketingWysiwyg.vue"

export default {
  name: "communicationDashboardParty",
  components: { MarketingToolHeader, "marketing-wysiwyg": MarketingWysiwyg, "slim-cropper": Slim },
  data() {
    return {
      parties: [],
      party: {},
      studios: [],
      showEditDialog: false,
      saving: false,
      loading: false,
      menuShow: false,
      menuHide: false,
      signupHeaders: [
        { text: "Vorname", value: "vorname" },
        { text: "Nachname", value: "nachname" },
        { text: "E-Mail", value: "email" },
      ],
      customToolbar: [[{ header: [false, 1, 2, 3] }], ["bold", "italic", "underline"], [{ list: "ordered" }, { list: "bullet" }], ["link"]],
      slimOptionsLandscape: {
        ratio: "3:1",
        service: this.uploadPictureLandscape,
        serviceFormat: "file",
        didInit: (data, slim) => {
          this.slimInstanceL = slim
        },
        label: "Slider Bild hochladen",
        initialImage: null,
      },
      slimOptionsPortrait: {
        ratio: "9:16",
        service: this.uploadPicturePortrait,
        serviceFormat: "file",
        didInit: (data, slim) => {
          this.slimInstanceP = slim
        },
        label: "Overlay Bild hochladen",
        initialImage: null,
      },
    }
  },
  computed: {
    upcomingParties() {
      return [...this.parties]
        .filter((item) => moment(item.eventDate).isSameOrAfter(moment(), "day"))
        .sort((a, b) => new Date(a.eventDate) - new Date(b.eventDate))
    },
    pastParties() {
      return [...this.parties]
        .filter((item) => moment(item.eventDate).isBefore(moment(), "day"))
        .sort((a, b) => new Date(b.eventDate) - new Date(a.eventDate))
    },
    totalSignups() {
      return this.parties.reduce((total, item) => total + ((item.signups && item.signups.length) || 0), 0)
    },
    signupEventCount() {
      return this.parties.filter((item) => item.allowSignup).length
    },
    formattedEventDate: {
      get() {
        if (!this.party.eventDate) return ""
        return moment(this.party.eventDate).format("YYYY-MM-DDTHH:mm")
      },
      set(value) {
        this.party.eventDate = value
      },
    },
  },
  created() {
    this.loadData()
  },
  methods: {
    async loadData() {
      const editingId = this.showEditDialog && this.party._id ? this.party._id : null
      this.loading = true
      try {
        this.studios = await api.fetchStudios()
        this.parties = await api.fetchAllParties()
        this.$emit("finishedloading")
        if (editingId) {
          const updated = this.parties.find((item) => item._id === editingId)
          if (updated) this.editParty(updated)
        }
      } catch (e) {
        this.$toast.error("Fehler beim Laden der Daten")
      } finally {
        this.loading = false
      }
    },
    createNewParty() {
      this.party = {
        title: "",
        description: "",
        image: "",
        imagePortrait: "",
        allowSignup: true,
        employeeOnly: false,
        maxSignups: 0,
        studios: [],
        signups: [],
        showAfter: moment().format("YYYY-MM-DD"),
        hideAfter: moment().add(1, "month").format("YYYY-MM-DD"),
        eventDate: null,
        location: "",
      }
      this.showEditDialog = true
    },
    editParty(item) {
      this.party = JSON.parse(JSON.stringify(item))
      if (this.party.showAfter) this.party.showAfter = moment(this.party.showAfter).format("YYYY-MM-DD")
      if (this.party.hideAfter) this.party.hideAfter = moment(this.party.hideAfter).format("YYYY-MM-DD")
      if (this.party.studios && this.party.studios.length && typeof this.party.studios[0] === "object")
        this.party.studios = this.party.studios.map((studio) => studio._id)
      if (!this.party.signups) this.party.signups = []
      this.showEditDialog = true
    },
    async saveParty() {
      if (!this.party.title || !this.party.eventDate) {
        this.$toast.error("Bitte Titel und Datum angeben.")
        return
      }
      this.saving = true
      try {
        const payload = JSON.parse(JSON.stringify(this.party))
        if (payload.signups && payload.signups.length && typeof payload.signups[0] === "object")
          payload.signups = payload.signups.map((user) => user._id)
        await api.saveParty(payload)
        this.showEditDialog = false
        await this.loadData()
        this.$toast.success("Event gespeichert!")
      } catch (e) {
        console.error(e)
        this.$toast.error("Fehler beim Speichern.")
      } finally {
        this.saving = false
      }
    },
    uploadPictureLandscape(formdata, progress, success, failure) {
      this.performUpload(formdata, success, failure, (name) => {
        this.party.image = name
      })
    },
    uploadPicturePortrait(formdata, progress, success, failure) {
      this.performUpload(formdata, success, failure, (name) => {
        this.party.imagePortrait = name
      })
    },
    performUpload(formdata, success, failure, callback) {
      const formData = new FormData()
      formData.append("file", formdata[0])
      api
        .uploadPartyImage(formData)
        .then((data) => {
          if (data.status) {
            callback(data.name)
            success(data)
          } else {
            this.$toast.error("Upload fehlgeschlagen")
            if (failure) failure(data)
          }
        })
        .catch((error) => {
          this.$toast.error("Fehler beim Upload")
          if (failure) failure(error)
        })
    },
  },
}
</script>

<style scoped>
.marketing-page {
  --surface: rgba(255, 255, 255, 0.035);
  --surface-strong: rgba(255, 255, 255, 0.055);
  --border: rgba(255, 255, 255, 0.1);
  --border-strong: rgba(255, 255, 255, 0.16);
  --text: #f4f4f5;
  --text-soft: #d4d4d8;
  --muted: #a3a3a8;
  max-width: 92.5rem;
  margin: 0 auto;
  padding: 1.5rem;
  color: var(--text);
  font-size: 1rem;
}

.icon-action {
  width: 2.75rem !important;
  min-width: 2.75rem !important;
  height: 2.75rem !important;
  border: 1px solid var(--border);
  border-radius: 0.8125rem !important;
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.055) !important;
}

.primary-action,
.secondary-action,
.cancel-action {
  min-height: 2.75rem !important;
  border-radius: 0.8125rem !important;
  font-size: 0.875rem !important;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

.primary-action {
  color: #ffffff !important;
}

.cancel-action {
  color: #ffffff !important;
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
  margin: 1rem 0;
}

.metric-card {
  display: flex;
  min-height: 6.75rem;
  align-items: center;
  gap: 0.875rem;
  padding: 1.125rem;
  border: 1px solid var(--border);
  border-radius: 1.1875rem !important;
  background: var(--surface) !important;
}

.metric-icon,
.editor-mark,
.section-icon,
.surface-empty > span {
  display: grid;
  flex: 0 0 auto;
  place-items: center;
}

.metric-icon {
  width: 2.625rem;
  height: 2.625rem;
  border-radius: 0.8125rem;
  color: #c5c5c8;
  background: rgba(255, 255, 255, 0.065);
}

.icon-green {
  color: #58d388;
  background: rgba(0, 150, 64, 0.12);
}

.icon-blue {
  color: #7dd3fc;
  background: rgba(14, 165, 233, 0.12);
}

.icon-amber {
  color: #fcd34d;
  background: rgba(245, 158, 11, 0.12);
}

.metric-card > div {
  display: grid;
  min-width: 0;
}

.metric-card > div > span {
  color: var(--text-soft);
  font-size: 0.875rem;
  font-weight: 700;
}

.metric-card strong {
  color: #ffffff;
  font-size: 1.6875rem;
  line-height: 1.15;
}

.metric-card small {
  margin-top: 0.1875rem;
  color: var(--muted);
  font-size: 0.75rem;
}

.content-surface {
  padding: 1.375rem;
  border: 1px solid var(--border);
  border-radius: 1.375rem !important;
  background: rgba(255, 255, 255, 0.03) !important;
  box-shadow: 0 1rem 2.75rem rgba(0, 0, 0, 0.12);
}

.archive-surface {
  margin-top: 1rem;
}

.surface-header,
.card-footer,
.event-editor-header,
.section-heading,
.editor-header-actions {
  display: flex;
  align-items: center;
}

.surface-header {
  justify-content: space-between;
  gap: 1.125rem;
  margin-bottom: 1.125rem;
}

.surface-eyebrow,
.section-eyebrow {
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.surface-header h2,
.section-heading h2 {
  margin: 0.1875rem 0 0;
  color: var(--text);
  font-size: 1.25rem;
}

.surface-header p,
.section-heading p,
.surface-empty p {
  margin: 0.25rem 0 0;
  color: var(--muted);
  font-size: 0.875rem;
  line-height: 1.5;
}

.count-pill,
.status-pill,
.signup-pill,
.muted-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
}

.count-pill {
  min-height: 1.75rem;
  padding: 0.25rem 0.625rem;
  border: 1px solid var(--border);
  color: var(--text-soft);
  background: rgba(255, 255, 255, 0.055);
  white-space: nowrap;
}

.event-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.875rem;
}

.event-card,
.archive-card {
  overflow: hidden;
  border: 1px solid var(--border);
  outline: none;
  background: var(--surface) !important;
  transition: transform 160ms ease, border-color 160ms ease, background-color 160ms ease;
}

.event-card {
  border-radius: 1.125rem !important;
}

.event-card:focus-visible,
.archive-card:focus-visible {
  border-color: rgba(0, 150, 64, 0.68);
  box-shadow: 0 0 0 3px rgba(0, 150, 64, 0.13);
}

.event-media {
  position: relative;
  height: 13.125rem;
  background: rgba(0, 0, 0, 0.2);
}

.event-image,
.image-placeholder {
  height: 100%;
}

.event-image h3,
.image-placeholder h3 {
  position: absolute;
  right: 1rem;
  bottom: 0.875rem;
  left: 1rem;
  margin: 0;
  color: #ffffff;
  font-size: 1.125rem;
  line-height: 1.3;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.7);
}

.image-placeholder,
.archive-placeholder {
  display: grid;
  place-items: center;
  color: #77797f;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.035), rgba(0, 0, 0, 0.18));
}

.image-placeholder .v-icon {
  color: currentColor;
  font-size: 3rem;
}

.status-pill {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  min-height: 1.75rem;
  gap: 0.3125rem;
  padding: 0.25rem 0.625rem;
  color: #ffffff;
}

.is-upcoming {
  background: rgba(0, 150, 64, 0.88);
}

.event-content {
  padding: 1rem;
}

.event-facts {
  display: grid;
  gap: 0.625rem;
  margin-bottom: 1rem;
}

.event-facts span {
  display: grid;
  grid-template-columns: 1.5rem minmax(0, 1fr);
  align-items: center;
  gap: 0.375rem;
  color: #c7c7cb;
  font-size: 0.875rem;
}

.event-facts .v-icon {
  color: #8f9095;
}

.card-footer {
  justify-content: space-between;
  gap: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.075);
}

.signup-pill,
.muted-pill {
  min-height: 1.75rem;
  gap: 0.3125rem;
  padding: 0.25rem 0.625rem;
}

.signup-pill {
  color: #58d388;
  background: rgba(0, 150, 64, 0.1);
}

.muted-pill {
  color: var(--muted);
  background: rgba(255, 255, 255, 0.045);
}

.signup-pill .v-icon,
.muted-pill .v-icon {
  color: currentColor;
}

.surface-empty {
  display: flex;
  min-height: 17rem;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
}

.surface-empty > span {
  width: 3.5rem;
  height: 3.5rem;
  margin-bottom: 0.75rem;
  border-radius: 1rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.11);
}

.surface-empty > span .v-icon {
  color: currentColor;
}

.surface-empty h3,
.archive-card h3 {
  margin: 0;
  color: var(--text);
  font-size: 1rem;
}

.surface-empty .secondary-action {
  margin-top: 1rem;
}

.archive-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.archive-card {
  display: grid;
  min-width: 0;
  grid-template-columns: 7rem minmax(0, 1fr) 1.5rem;
  align-items: center;
  gap: 0.75rem;
  border-radius: 1rem !important;
  opacity: 0.82;
}

.archive-image,
.archive-placeholder {
  width: 7rem;
  height: 5.5rem;
}

.archive-card > div {
  min-width: 0;
}

.archive-card > div > span {
  display: inline-flex;
  align-items: center;
  gap: 0.3125rem;
  color: var(--muted);
  font-size: 0.75rem;
}

.archive-card h3 {
  margin-top: 0.375rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.event-editor {
  --surface: rgba(255, 255, 255, 0.035);
  --surface-strong: rgba(255, 255, 255, 0.055);
  --border: rgba(255, 255, 255, 0.1);
  --border-strong: rgba(255, 255, 255, 0.16);
  --text: #f4f4f5;
  --text-soft: #d4d4d8;
  --muted: #a3a3a8;
  display: flex;
  width: 100%;
  height: 100vh;
  height: 100dvh;
  min-height: 0;
  flex-direction: column;
  overflow: hidden;
  color: var(--text);
  background: #161617 !important;
  font-size: 1rem;
}

.event-editor-header {
  position: sticky;
  z-index: 5;
  top: 0;
  min-height: 6rem;
  flex: 0 0 auto;
  gap: 0.875rem;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--border);
  background: linear-gradient(115deg, rgba(0, 150, 64, 0.07), transparent 48%), rgba(24, 24, 25, 0.97);
  backdrop-filter: blur(1rem);
}

.editor-mark {
  width: 3rem;
  height: 3rem;
  border: 1px solid rgba(0, 150, 64, 0.26);
  border-radius: 0.875rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.12);
}

.editor-mark .v-icon,
.section-icon .v-icon {
  color: currentColor;
}

.editor-title {
  min-width: 0;
}

.editor-title h1 {
  margin: 0.125rem 0 0;
  color: #ffffff;
  font-size: 1.375rem;
}

.editor-title p {
  margin: 0.1875rem 0 0;
  color: var(--muted);
  font-size: 0.875rem;
}

.editor-header-actions {
  gap: 0.5rem;
  margin-left: auto;
}

.event-editor-body {
  min-height: 0;
  flex: 1 1 auto;
  padding: 1.25rem !important;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

.editor-layout {
  display: grid;
  max-width: 92.5rem;
  grid-template-columns: minmax(20rem, 0.72fr) minmax(0, 1.28fr);
  gap: 1rem;
  margin: 0 auto;
}

.editor-column {
  display: grid;
  align-content: start;
  gap: 1rem;
}

.editor-section {
  min-width: 0;
  padding: 1.125rem;
  border: 1px solid var(--border);
  border-radius: 1.25rem;
  background: rgba(255, 255, 255, 0.03);
}

.section-heading {
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.section-heading-actions .icon-action {
  margin-left: auto;
}

.section-icon {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.75rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.11);
}

.event-cropper {
  width: 100%;
  overflow: hidden;
  border-radius: 0.875rem;
}

.media-upload-list {
  display: grid;
  gap: 0.75rem;
}

.cropper-panel {
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.075);
  border-radius: 0.875rem;
  background: rgba(0, 0, 0, 0.12);
}

.cropper-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.625rem;
}

.cropper-heading strong {
  color: var(--text);
  font-size: 0.875rem;
}

.cropper-heading span {
  color: var(--muted);
  font-size: 0.75rem;
}

.portrait-cropper {
  max-width: 12rem;
  margin: 0 auto;
}

.modern-field {
  margin-bottom: 0.75rem;
}

.modern-field ::v-deep .v-input__slot {
  border-radius: 0.8125rem !important;
  background: rgba(255, 255, 255, 0.035) !important;
}

.modern-field ::v-deep fieldset {
  border-color: rgba(255, 255, 255, 0.15) !important;
}

.modern-field ::v-deep .v-label,
.modern-switch ::v-deep .v-label {
  color: #b8b8bc !important;
  font-size: 0.875rem;
}

.option-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
}

.option-card {
  min-height: 6.5rem;
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.075);
  border-radius: 0.8125rem;
  background: rgba(0, 0, 0, 0.12);
}

.option-card > span {
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.capacity-field {
  margin-top: 0.5rem;
}

.participant-table {
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 0.875rem;
  background: transparent !important;
}

.participant-table ::v-deep th,
.participant-table ::v-deep td {
  font-size: 0.75rem !important;
}

.participant-email {
  color: var(--muted);
}

.mobile-save-bar {
  display: none;
}

@media (hover: hover) and (pointer: fine) {
  .event-card:hover,
  .archive-card:hover {
    transform: translateY(-2px);
    border-color: var(--border-strong);
    background: var(--surface-strong) !important;
  }
}

@media (max-width: 78.9375rem) {
  .marketing-page {
    padding: 1.125rem;
  }

  .event-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .editor-layout {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (max-width: 59.9375rem) {
  .metric-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .option-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 37.4375rem) {
  .marketing-page {
    padding: 0.75rem;
  }

  .metric-grid {
    gap: 0.5rem;
    margin: 0.75rem 0;
  }

  .metric-card {
    min-height: 6.5rem;
    align-items: flex-start;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.75rem;
    border-radius: 0.9375rem !important;
  }

  .metric-card small {
    display: none;
  }

  .content-surface {
    padding: 0.875rem;
    border-radius: 1.125rem !important;
  }

  .surface-header {
    align-items: flex-start;
    flex-direction: column;
    gap: 0.625rem;
  }

  .event-grid,
  .archive-grid,
  .option-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .archive-card {
    grid-template-columns: 5rem minmax(0, 1fr) 1.5rem;
  }

  .archive-image,
  .archive-placeholder {
    width: 5rem;
  }

  .event-editor-header {
    min-height: 5rem;
    padding: 0.75rem;
  }

  .editor-mark,
  .editor-title p,
  .editor-header-actions .cancel-action,
  .editor-header-actions .primary-action {
    display: none;
  }

  .editor-title h1 {
    font-size: 1.125rem;
  }

  .event-editor-body {
    padding: 0.75rem 0.75rem 5.5rem !important;
  }

  .editor-layout,
  .editor-column {
    gap: 0.75rem;
  }

  .editor-section {
    padding: 0.875rem;
    border-radius: 1rem;
  }

  .mobile-save-bar {
    position: fixed;
    z-index: 6;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    gap: 0.5rem;
    padding: 0.75rem 0.75rem max(0.75rem, env(safe-area-inset-bottom));
    border-top: 1px solid var(--border);
    background: rgba(24, 24, 25, 0.97);
    backdrop-filter: blur(1rem);
  }

  .mobile-save-bar .v-btn {
    flex: 1 1 50%;
  }
}
</style>
